// Maps the app's view ids to browser paths and back, so a reload (or a shared link) opens the
// same screen instead of falling back to the dashboard.

export interface RouteState {
  view: string;
  taskId: string | null;
  channelId: string | null;
}

const SIMPLE_VIEW_PATHS: Record<string, string> = {
  dashboard: '/',
  projects: '/projects',
  boards: '/boards',
  'my-tasks': '/my-tasks',
  workload: '/workload',
  team: '/team',
  chat: '/chat',
  meetings: '/meetings',
  teams: '/teams',
  audit: '/audit',
  roles: '/roles',
};

const PATH_VIEWS: Record<string, string> = Object.fromEntries(
  Object.entries(SIMPLE_VIEW_PATHS).map(([view, path]) => [path, view])
);

export function stateToPath({ view, taskId, channelId }: RouteState): string {
  if (taskId) return `/tasks/${encodeURIComponent(taskId)}`;
  if (view.startsWith('project-')) return `/projects/${encodeURIComponent(view.slice('project-'.length))}`;
  if (view.startsWith('team-')) return `/teams/${encodeURIComponent(view.slice('team-'.length))}`;
  const path = SIMPLE_VIEW_PATHS[view] || '/';
  return view === 'chat' && channelId ? `${path}?channelId=${encodeURIComponent(channelId)}` : path;
}

export function pathToState(pathname: string, search = ''): RouteState {
  const path = pathname.replace(/\/+$/, '') || '/';
  const state: RouteState = { view: 'dashboard', taskId: null, channelId: null };

  const task = path.match(/^\/tasks\/([^/]+)$/);
  if (task) {
    // A task link shows the task over the boards screen
    return { ...state, view: 'boards', taskId: decodeURIComponent(task[1]) };
  }
  const project = path.match(/^\/projects\/([^/]+)$/);
  if (project) return { ...state, view: `project-${decodeURIComponent(project[1])}` };
  const team = path.match(/^\/teams\/([^/]+)$/);
  if (team) return { ...state, view: `team-${decodeURIComponent(team[1])}` };

  if (PATH_VIEWS[path]) {
    const channelId = path === '/chat' ? new URLSearchParams(search).get('channelId') : null;
    return { ...state, view: PATH_VIEWS[path], channelId };
  }
  return state;
}
